import * as React from 'react';
import * as classnames from 'classnames';
import Dropzone from 'react-dropzone';
import { ProjectLanguage } from '@nexxt/common/types';
import {
    importCSV,
    preprocessingTranslation
} from '../../../services/project.service';
import Checkbox from '../../common/Checkbox';
import Loader from '../../common/Loader/Loader';
import Modal, {
    ModalBody,
    ModalFooter,
    ModalHeader,
    ModalTitle
} from '../../common/modal';
import { UilUploadAlt } from '@iconscout/react-unicons';
import {
    UisExclamationTriangle,
    UisCheckCircle
} from '@iconscout/react-unicons-solid';
interface TranslationImportModalProps {
    isOpen: boolean;
    projectId: number;
    languages: ProjectLanguage[];
    preprocessing?: () => void;
    onClose: () => void;
}

const acceptFormats = {
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': [
        '.xlsx'
    ]
};

const TranslationImportModal: React.FunctionComponent<TranslationImportModalProps> =
    (props: TranslationImportModalProps) => {
        const [isLoading, setIsLoading] = React.useState(false);
        const [languageChoice, setLanguageChoice] = React.useState({});
        const [imported, setImported] = React.useState(false);
        const [file, setFile] = React.useState(null);
        const [problems, setProblems] = React.useState<
            { cell?: string; message: string }[]
        >([]);
        React.useEffect(() => {
            setLanguageChoice(
                props.languages.reduce((obj, l) => {
                    return { ...obj, [l.value]: true };
                }, {})
            );
        }, [props.languages]);

        React.useEffect(() => {
            async function validateTranslation() {
                setIsLoading(true);
                setProblems([]);
                await preprocessingTranslation(
                    props.projectId,
                    file,
                    languageChoice
                );
                const problems = await preprocessingTranslation(
                    props.projectId,
                    file,
                    languageChoice
                );
                setProblems(problems);
                setIsLoading(false);
            }

            if (file) {
                validateTranslation();
            }
        }, [languageChoice, file]);
        const processTranslation = (accepted, rejected) => {
            if (accepted) {
                setFile(accepted[0]);
            }
        };

        const importTranslation = async () => {
            setIsLoading(true);

            await importCSV(props.projectId, file, languageChoice);

            setIsLoading(false);
            setImported(true);
        };
        const uploadBody = () => {
            if (isLoading) {
                return <Loader isInline={false} />;
            }

            return imported ? (
                <div style={{ textAlign: 'center' }}>
                    <h3>
                        <b>Upload Complete!</b>
                    </h3>
                    <UisCheckCircle color="#20883D" size={40} />
                    <p>
                        You may now go to your project to see the new project
                        texts
                    </p>
                </div>
            ) : (
                <div>
                    <Dropzone
                        accept={acceptFormats}
                        onDrop={processTranslation}
                        multiple={false}
                    >
                        {({
                            getRootProps,
                            getInputProps,
                            isDragActive
                        }: any) => (
                            <label
                                {...getRootProps({
                                    className: classnames('file--upload', {
                                        uploaded: !!file,
                                        ['drag-active']: isDragActive
                                    })
                                })}
                            >
                                <input {...getInputProps()} />
                                {file ? (
                                    <span>{file.name}</span>
                                ) : (
                                    <>
                                        <span>
                                            Please upload your file here...
                                        </span>
                                        <UilUploadAlt size={30} />
                                    </>
                                )}
                            </label>
                        )}
                    </Dropzone>
                    <p className="translation--info-message">
                        file type must be .xlsx
                    </p>

                    <div>
                        {props.languages.map((l) => (
                            <div key={`${props.projectId}-${l.value}`}>
                                <Checkbox
                                    text={`Apply ${l.label} translations`}
                                    onChange={(e) => {
                                        setLanguageChoice((languageChoice) => ({
                                            ...languageChoice,
                                            [l.value]: e.target.checked
                                        }));
                                    }}
                                    value={l.value}
                                    defaultChecked={languageChoice[l.value]}
                                    disabled={false}
                                />
                            </div>
                        ))}
                    </div>
                    {problems.length ? (
                        <div className="translation--warning--container">
                            <p className="translation--warning--list-title">
                                <UisExclamationTriangle
                                    color="#F7CA18"
                                    size={'24'}
                                />{' '}
                                Warnings:
                            </p>
                            {problems.map((problem, i) => (
                                <li
                                    className="translation--warning--list-item"
                                    key={`translation-error-${props.projectId}-${i}`}
                                >
                                    {problem.message}(<b>{problem?.cell}</b>)
                                </li>
                            ))}
                        </div>
                    ) : null}
                </div>
            );
        };
        return (
            <Modal isOpen={props.isOpen} className="modal-dialog">
                <ModalHeader onClose={props.onClose}>
                    <ModalTitle>Import Question/Option</ModalTitle>
                </ModalHeader>
                <ModalBody>{uploadBody()}</ModalBody>
                <ModalFooter>
                    <button
                        onClick={props.onClose}
                        className="btn btn--sm btn--transparent"
                    >
                        Close
                    </button>

                    {!imported ? (
                        <button
                            onClick={importTranslation}
                            className="btn btn--sm btn--green"
                            disabled={!file || isLoading}
                        >
                            Import
                        </button>
                    ) : null}
                </ModalFooter>
            </Modal>
        );
    };

export default TranslationImportModal;
